import { create } from 'zustand'

import { CalculateMarketShopCartRequest } from '@/api/market-shop/checkout/get-calculated-market-shop-cart'
import { popupFactory } from '@/factories'
import { MarketShopProductModel } from '@/models'
import { createMarketShopSessionId } from '@/pages/market-shop/market-shop.utils'

export type MarketShopStore = {
  sessionId: string
  cart: Record<
    MarketShopProductModel['id'],
    {
      product: MarketShopProductModel
      quantity: number
      note?: string
    }
  >
  note: string
  resetCount: number
  isOpened: boolean
  addProduct: (product: MarketShopProductModel) => void
  setProductQuantity: (product: MarketShopProductModel, newQuantity: number, note?: string) => void
  setProductNote: (product: MarketShopProductModel, note: string) => void
  setIsOpened: (isOpened: boolean) => void
  removeProduct: (product: MarketShopProductModel) => void
  getQuantity: (product: MarketShopProductModel) => number
  setNote: (note: string) => void
  subtractProduct: (product: MarketShopProductModel) => void
  getTotalProductsCountInCart: () => number
  hasProductsInCart: () => boolean
  getMappedCartRequest: () => CalculateMarketShopCartRequest
  reset: VoidFunction
}

export const useMarketShopStore = create<MarketShopStore>((set, getState) => ({
  addProduct: (product: MarketShopProductModel) => {
    const state = getState()
    const newQuantity = state.getQuantity(product) + 1
    state.setProductQuantity(product, newQuantity)
  },
  cart: {},
  getMappedCartRequest: () => {
    const state = getState()

    return {
      cart: {
        items: Object.values(state.cart).map(({ product, quantity, note }) => ({
          productId: product.id,
          quantity,
          ...(note && { note }),
        })),
      },
      sessionId: state.sessionId,
    }
  },
  getNote: () => {
    const state = getState()
    return state.note
  },
  getQuantity: (product: MarketShopProductModel) => {
    if (!product) return 0
    const state = getState()
    return state.cart[product.id]?.quantity ?? 0
  },
  getTotalProductsCountInCart: () => {
    const state = getState()
    return Object.values(state.cart).reduce((acc, { quantity }) => acc + quantity, 0)
  },
  hasProductsInCart: () => {
    const state = getState()
    return Object.keys(state.cart).length > 0
  },
  isOpened: true,
  note: '',
  removeProduct: (product: MarketShopProductModel) => {
    set((state) => {
      const { [product.id]: __removed, ...restCart } = state.cart
      return { cart: restCart }
    })
  },
  reset: () => {
    // TODO: sync with Igor's parking management logic about stacking popups
    popupFactory.clear(true)

    set((state) => {
      return {
        cart: {},
        note: '',
        resetCount: state.resetCount + 1,
        sessionId: createMarketShopSessionId(),
      }
    })
  },
  resetCount: 0,
  sessionId: createMarketShopSessionId(),
  setIsOpened: (isOpened) => {
    set({ isOpened })
  },
  setNote: (note) => {
    set({
      note: note,
    })
  },
  setProductNote: (product: MarketShopProductModel, note: string) => {
    const state = getState()
    const existing = state.cart[product.id]
    if (!existing) return
    set({ cart: { ...state.cart, [product.id]: { ...existing, note: note || undefined } } })
  },
  setProductQuantity: (product: MarketShopProductModel, newQuantity: number, note?: string) => {
    const state = getState()
    if (newQuantity <= 0) return state.removeProduct(product)
    const existing = state.cart[product.id]
    const resolvedNote = note !== undefined ? note || undefined : existing?.note
    set({
      cart: { ...state.cart, [product.id]: { note: resolvedNote, product, quantity: newQuantity } },
    })
  },
  subtractProduct: (product: MarketShopProductModel) => {
    const state = getState()
    const newQuantity = state.getQuantity(product) - 1
    if (newQuantity <= 0) return state.removeProduct(product)
    state.setProductQuantity(product, newQuantity)
  },
}))
